import Image from 'next/image'
import cx from 'classnames'
import styles from './HeroV2.module.scss'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { Button, Heading } from '~elements'
import Video from 'components/elements/Video/Video'
import { ButtonProps, ResponsiveMultimedia } from '~types'
import { SizeBreakpointLg, SizeBreakpointSm } from '~styles/dist/tokens.mjs'
import { useWindowSize } from 'utils/hooks'
import { getGtmLinkPosition } from '~analyticsUtils'
import { getPortalRoot } from '~utils/index'
import ReactDOM from 'react-dom'
import { useEffect, useRef, useState } from 'react'

export type HeroV2Props = {
  alignHeadline?: 'center' | 'bottom'
  description?: string
  responsiveMultimedia?: ResponsiveMultimedia
  subtitle?: string
  title?: string
  showTitle?: boolean
  justify?: 'center' | 'left' | 'right'
  ctaButtons?: ButtonProps[]
  isChildOfHeroCarousel?: boolean
  heroHeight?: 'spacious' | 'compact' | 'full'
  alignButtonGroupBottom?: boolean
}

const HeroV2 = ({
  alignHeadline,
  description,
  title,
  subtitle,
  responsiveMultimedia,
  ctaButtons,
  justify = 'left',
  showTitle,
  isChildOfHeroCarousel = false,
  heroHeight = 'spacious',
  alignButtonGroupBottom = false
}: HeroV2Props) => {
  const heroRef = useRef<HTMLDivElement | null>(null)
  const [isHeroOutOfView, setIsHeroOutOfView] = useState(false)
  const portalRoot = getPortalRoot()
  const linkLocation = isChildOfHeroCarousel ? 'hero_carousel' : ''
  const linkModule = 'hero'
  const { isBreakpoint: isBelowXs } = useWindowSize('xs', '<')
  const { isBreakpoint: isBelowMd } = useWindowSize('sm', '<')
  const showHeadline = !!showTitle || !!subtitle || !!description || !!(ctaButtons && ctaButtons.length > 0)
  // Determine if CTA should appear at bottom (without headline content above)
  const hasLowerCta =
    !showTitle && !subtitle && !description && !!ctaButtons && ctaButtons.length > 0 && heroHeight !== 'full'
  const lowerHeadline = hasLowerCta || alignHeadline === 'bottom'
  const hasCtaButton = ctaButtons && ctaButtons.length > 0
  const hasTwoButtons = hasCtaButton && ctaButtons.length > 1
  //Determine if CTA should be fixed only when there is 1 cta
  const hasFixedCta = alignButtonGroupBottom && heroHeight === 'full' && ctaButtons?.length === 1
  const shouldCenterContent = justify === 'center'
  const shortHero = heroHeight === 'compact'

  const {
    posterImageSm,
    posterImageXl,
    backgroundVideoSm,
    backgroundVideoXl,
    allowPause,
    allowVolume,
    vimeoIdXl,
    vimeoIdSm
  } = responsiveMultimedia ?? {}
  const fallbackImage = posterImageSm ?? posterImageXl
  const fallbackAlt = posterImageSm?.altText ?? posterImageXl?.altText ?? title ?? ''

  const hasVideo = Boolean(vimeoIdXl ?? vimeoIdSm ?? backgroundVideoXl ?? backgroundVideoSm)

  useEffect(() => {
    if (!heroRef.current || !isBelowMd) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroOutOfView(!entry.isIntersecting)
      },
      { threshold: 0 }
    )

    observer.observe(heroRef.current)
    return () => observer.disconnect()
  }, [isBelowMd])

  const StickyCtaButton = () => {
    if (!ctaButtons?.length || !portalRoot || !isHeroOutOfView) return null
    const primaryButton = ctaButtons[0]

    return ReactDOM.createPortal(
      <div
        className={cx('portal-fixed-cta-container', {
          'portal-fixed-cta-container--visible': isHeroOutOfView
        })}
      >
        <Button
          {...primaryButton}
          isFullWidth
          analytics={{
            linkModule,
            clickTracking: `${linkModule}:${title}:${primaryButton.title}:sticky_cta`,
            linkPosition: 'sticky_bottom',
            linkLocation
          }}
        />
      </div>,
      portalRoot
    )
  }

  const CtaButton = () => {
    const buttons = (
      <div
        className={cx(
          styles['btn-container'],
          !shortHero && hasTwoButtons && styles['btn-container--has-two-buttons'],
          shouldCenterContent && styles['btn-container-center'],
          shortHero && styles['btn-container-top-right']
        )}
      >
        {ctaButtons?.map((button, index) => (
          <Button
            key={`${button.title}_${index}`}
            {...button}
            {...(isBelowXs &&
              hasTwoButtons && {
                size: 'md',
                isFullWidth: true
              })}
            analytics={{
              linkModule,
              clickTracking: `${linkModule}:${title}:${button.title}:cta`,
              linkPosition: getGtmLinkPosition(ctaButtons, index),
              linkLocation
            }}
          />
        ))}
      </div>
    )

    return buttons
  }

  return (
    <>
      <div ref={heroRef} className={styles['hero']}>
        <div
          className={cx(
            styles['hero-container'],
            title && !showTitle && styles['hero-container__hidden-title'],
            heroHeight === 'compact' && styles['hasCompactHeight'],
            heroHeight === 'full' && styles['hasFullHeight'],
            (hasVideo || fallbackImage) && styles['hasBgGradient']
          )}
        >
          <div className={styles['hero__media-wrapper']}>
            {hasVideo ? (
              <Video
                backgroundVideoXl={backgroundVideoXl}
                backgroundVideoSm={backgroundVideoSm}
                posterImageXl={posterImageXl}
                posterImageSm={posterImageSm}
                autoPlay={true}
                videoLoop={Boolean(responsiveMultimedia?.videoLoop)}
                allowPause={allowPause}
                allowVolume={allowVolume}
                vimeoIdXl={vimeoIdXl}
                vimeoIdSm={vimeoIdSm}
                isChildOfHeroCarousel={isChildOfHeroCarousel}
              />
            ) : (
              responsiveMultimedia && (
                <picture>
                  {posterImageXl?.url && (
                    <source srcSet={posterImageXl.url} media={`(min-width: ${SizeBreakpointLg}px)`} />
                  )}
                  {posterImageSm?.url && (
                    <source srcSet={posterImageSm.url} media={`(min-width: ${SizeBreakpointSm}px)`} />
                  )}
                  {fallbackImage?.url && (
                    <Image
                      src={fallbackImage?.url}
                      alt={fallbackAlt}
                      sizes='100vw'
                      fill
                      width={0}
                      height={0}
                      style={{
                        width: '100%',
                        height: '100%'
                      }}
                      quality={NEXT_IMAGE_QUALITY}
                      priority
                    />
                  )}
                </picture>
              )
            )}
          </div>

          {title && !showTitle && (
            <Heading level={1} justify='left' showHeading={showTitle}>
              {title}
            </Heading>
          )}

          {showHeadline && (
            <div
              className={cx(
                styles['headline-container'],
                shouldCenterContent && styles['headline-center'],
                lowerHeadline && styles['headline-container__lower'],
                hasLowerCta && styles['headline-container__lower-cta-only'],
                hasFixedCta && styles['headline-container__fixed-cta'],
                alignButtonGroupBottom && styles['headline-container__fixed-cta__bottom']
              )}
            >
              {subtitle && (
                <Heading level={4} justify='left'>
                  {subtitle}
                </Heading>
              )}

              {title && showTitle && (
                <Heading level={1} levelDisplay={isBelowMd ? 2 : 'display'} justify='left' noMarginBottom noMarginTop>
                  {title}
                </Heading>
              )}

              {description && (
                <Heading level={5} justify='left'>
                  {description}
                </Heading>
              )}

              {hasCtaButton && <CtaButton />}
            </div>
          )}
        </div>
      </div>
      {isBelowMd && heroHeight === 'full' && <StickyCtaButton />}
    </>
  )
}

export default HeroV2
